import crud from './helpers/crud';
import { savedViewsEnabledForPage } from '../containers/records/shared/savedViews/savedViewsAccess';

// ENG-3551 personal saved views (`/api/v1/saved_inbox_views`). Every action takes
// the mounted page's context `{ userId, orgId, page, enabled }` and is a no-op —
// no request, no dispatch — unless that context is enabled AND the cached
// saved_views flag still allows the page. Lists are keyed by user/org/page, so a
// reply for another identity or page can never land in the list being shown.

export const SAVED_VIEWS = {
  FETCH_BEGIN: 'BEGIN_FETCH_SAVED_VIEWS',
  FETCH_SUCCESS: 'FINISHED_FETCH_SAVED_VIEWS',
  FETCH_FAIL: 'FAILED_FETCH_SAVED_VIEWS',
  CREATE_BEGIN: 'BEGIN_CREATE_SAVED_VIEW',
  CREATE_SUCCESS: 'FINISHED_CREATE_SAVED_VIEW',
  CREATE_FAIL: 'FAILED_CREATE_SAVED_VIEW',
  UPDATE_BEGIN: 'BEGIN_UPDATE_SAVED_VIEW',
  UPDATE_SUCCESS: 'FINISHED_UPDATE_SAVED_VIEW',
  UPDATE_FAIL: 'FAILED_UPDATE_SAVED_VIEW',
  DELETE_BEGIN: 'BEGIN_DELETE_SAVED_VIEW',
  DELETE_SUCCESS: 'FINISHED_DELETE_SAVED_VIEW',
  DELETE_FAIL: 'FAILED_DELETE_SAVED_VIEW',
};

const SAVED_VIEWS_URL = '/api/v1/saved_inbox_views';

export const savedViewsListKey = ({ userId, orgId, page }) => `${userId}|${orgId}|${page}`;

const isLive = (context, page) => Boolean(
  context
  && context.enabled === true
  && context.page === page
  && savedViewsEnabledForPage(page),
);

const SKIPPED = { skipped: true };

const contextMeta = ({ userId, orgId, page }) => ({ userId, orgId, page });

// A list fetch that started before a later successful mutation must not
// overwrite it; the reducer accepts a fetch reply only for the latest requestId.
let nextSavedViewsRequestId = 0;

export const fetchSavedViews = (page, context) => (dispatch) => {
  if (!isLive(context, page)) return Promise.resolve(SKIPPED);
  nextSavedViewsRequestId += 1;
  return dispatch(crud({
    dispatch: {
      begin: SAVED_VIEWS.FETCH_BEGIN,
      fail: SAVED_VIEWS.FETCH_FAIL,
      end: SAVED_VIEWS.FETCH_SUCCESS,
      meta: { ...contextMeta(context), requestId: nextSavedViewsRequestId },
    },
    method: 'GET',
    url: `${SAVED_VIEWS_URL}?page=${encodeURIComponent(page)}`,
  }));
};

// Mutations can commit before their response is rendered, so the transport's
// automatic 503 replay is disabled for all three.
export const createSavedView = (page, name, filters, context) => (dispatch) => {
  if (!isLive(context, page)) return Promise.resolve(SKIPPED);
  return dispatch(crud({
    dispatch: {
      begin: SAVED_VIEWS.CREATE_BEGIN,
      fail: SAVED_VIEWS.CREATE_FAIL,
      end: SAVED_VIEWS.CREATE_SUCCESS,
      meta: contextMeta(context),
    },
    method: 'POST',
    url: SAVED_VIEWS_URL,
    data: { saved_inbox_view: { page, name, filters } },
    retryOnUnavailable: false,
  }));
};

export const updateSavedView = (id, attrs, context) => (dispatch) => {
  if (!isLive(context, context?.page)) return Promise.resolve(SKIPPED);
  return dispatch(crud({
    dispatch: {
      begin: SAVED_VIEWS.UPDATE_BEGIN,
      fail: SAVED_VIEWS.UPDATE_FAIL,
      end: SAVED_VIEWS.UPDATE_SUCCESS,
      meta: { ...contextMeta(context), id },
    },
    method: 'PATCH',
    url: `${SAVED_VIEWS_URL}/${encodeURIComponent(id)}`,
    data: { saved_inbox_view: attrs },
    retryOnUnavailable: false,
  }));
};

export const deleteSavedView = (id, context) => (dispatch) => {
  if (!isLive(context, context?.page)) return Promise.resolve(SKIPPED);
  return dispatch(crud({
    dispatch: {
      begin: SAVED_VIEWS.DELETE_BEGIN,
      fail: SAVED_VIEWS.DELETE_FAIL,
      end: SAVED_VIEWS.DELETE_SUCCESS,
      meta: { ...contextMeta(context), id },
    },
    method: 'DELETE',
    url: `${SAVED_VIEWS_URL}/${encodeURIComponent(id)}`,
    retryOnUnavailable: false,
  }));
};
